import React, { useState, useEffect } from "react";

export default function LandingPage({ onGetStarted, lang = "fr" }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 80); return () => clearTimeout(t); }, []);

  const lp = {
    fr: {
      tagline: "Le carnet de bord numérique\nde votre van.",
      desc: "Checklist de départ, dossiers, dépenses voyage, autonomie, outils de terrain — tout ce dont un vaniste a besoin, dans sa poche.",
      scoreLabel: "Score véhicule",
      scoreDesc: "Votre van, toujours prêt au départ",
      cta: "Commencer — C'est gratuit",
      signin: "Déjà un compte ? Se connecter",
      secure: "Données sécurisées",
      offline: "Fonctionne hors ligne",
      multidevice: "Multi-appareils",
      featuresTitle: "Tout ce qu'il vous faut",
      ctaBottom: "Essayer CheckVan gratuitement",
      noCB: "Plan gratuit disponible · Sans carte bancaire",
      features: [
        { icon: "✅", title: "Checklist complète", desc: "Checklist véhicule, départ hivernal et sécurité. Ne partez plus jamais sans être prêt." },
        { icon: "📁", title: "Dossiers & rappels", desc: "Assurance, contrôle technique, garage — tous vos documents avec rappels automatiques." },
        { icon: "💰", title: "Dépenses voyage", desc: "Carburant, péage, camping, électricité — suivez chaque euro de vos voyages." },
        { icon: "🧰", title: "Outils de terrain", desc: "Niveau à bulle, inclinomètre de sécurité, boussole solaire — les capteurs de votre smartphone au service du van." },
        { icon: "📊", title: "Rapport & QR Code", desc: "Générez un passeport numérique de votre van partageable en un clic." },
        { icon: "🆘", title: "Bouton SOS", desc: "Accès rapide aux numéros d'urgence et services proches, partout sur la route." },
      ],
    },
    en: {
      tagline: "The digital logbook\nfor your van.",
      desc: "Departure checklist, documents, travel expenses, autonomy, field tools — everything a van lifer needs, in their pocket.",
      scoreLabel: "Vehicle score",
      scoreDesc: "Your van, always ready to go",
      cta: "Get Started — It's Free",
      signin: "Already have an account? Sign in",
      secure: "Secure data",
      offline: "Works offline",
      multidevice: "Multi-device",
      featuresTitle: "Everything you need",
      ctaBottom: "Try CheckVan for Free",
      noCB: "Free plan available · No credit card required",
      features: [
        { icon: "✅", title: "Full checklist", desc: "Vehicle, winter departure and safety checklists. Never leave unprepared again." },
        { icon: "📁", title: "Documents & reminders", desc: "Insurance, MOT, garage — all your documents with automatic reminders." },
        { icon: "💰", title: "Travel expenses", desc: "Fuel, tolls, camping, electricity — track every euro of your trips." },
        { icon: "🧰", title: "Field tools", desc: "Spirit level, safety inclinometer, solar compass — your smartphone sensors for van life." },
        { icon: "📊", title: "Report & QR Code", desc: "Generate a shareable digital passport for your van in one tap." },
        { icon: "🆘", title: "SOS Button", desc: "Quick access to emergency numbers and nearby services, anywhere on the road." },
      ],
    },
  }[lang] || {};

  return (
    <div style={{
      maxWidth: 430, margin: "0 auto", minHeight: "100vh",
      background: "#1C3A28", color: "#F8F6F1",
      fontFamily: "-apple-system, 'SF Pro Display', 'Segoe UI', sans-serif",
      overflowX: "hidden",
      opacity: visible ? 1 : 0, transition: "opacity 0.5s ease",
    }}>

      {/* Hero */}
      <div style={{
        padding: "60px 24px 40px", textAlign: "center",
        background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(45,90,61,0.6) 0%, transparent 70%)",
        borderBottom: "1px solid rgba(248,246,241,0.06)",
      }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>🚐</div>
        <div style={{ fontSize: 32, fontWeight: 900, color: "#9FE1CB", letterSpacing: 5, marginBottom: 8 }}>
          CHECKVAN
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#F8F6F1", marginBottom: 10, lineHeight: 1.3, whiteSpace: "pre-line" }}>
          {lp.tagline}
        </div>
        <div style={{ fontSize: 14, color: "rgba(248,246,241,0.55)", marginBottom: 32, lineHeight: 1.6, maxWidth: 320, margin: "0 auto 32px" }}>
          {lp.desc}
        </div>

        {/* Score badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 10,
          background: "rgba(159,225,203,0.1)", border: "1px solid rgba(159,225,203,0.3)",
          borderRadius: 50, padding: "10px 22px", marginBottom: 32,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: "50%",
            background: "conic-gradient(#9FE1CB 0% 88%, rgba(255,255,255,0.08) 88% 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 800, color: "#9FE1CB",
          }}>88</div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#F8F6F1" }}>{lp.scoreLabel}</div>
            <div style={{ fontSize: 11, color: "rgba(248,246,241,0.45)" }}>{lp.scoreDesc}</div>
          </div>
        </div>
      </div>

      {/* Trust bar */}
      <div style={{
        display: "flex", justifyContent: "space-around",
        padding: "20px 16px", borderBottom: "1px solid rgba(248,246,241,0.06)",
      }}>
        {[
          { icon: "🔒", label: lp.secure },
          { icon: "📱", label: lp.offline },
          { icon: "🌍", label: lp.multidevice },
        ].map(item => (
          <div key={item.label} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{item.icon}</div>
            <div style={{ fontSize: 11, color: "rgba(248,246,241,0.45)" }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* Features */}
      <div style={{ padding: "32px 20px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#9FE1CB", letterSpacing: 2, textTransform: "uppercase", marginBottom: 20, textAlign: "center" }}>
          {lp.featuresTitle}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {lp.features?.map((f, i) => (
            <div key={i} style={{
              display: "flex", gap: 14,
              background: "rgba(248,246,241,0.03)", border: "1px solid rgba(248,246,241,0.06)",
              borderRadius: 14, padding: "14px 16px",
            }}>
              <div style={{ fontSize: 24, flexShrink: 0, marginTop: 2 }}>{f.icon}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#F8F6F1", marginBottom: 3 }}>{f.title}</div>
                <div style={{ fontSize: 12, color: "rgba(248,246,241,0.45)", lineHeight: 1.5 }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA bottom */}
      <div style={{ padding: "20px 20px 48px" }}>
        <button
          onClick={onGetStarted}
          style={{
            width: "100%", padding: "16px 0",
            background: "#2D5A3D", color: "#F8F6F1",
            border: "2px solid #9FE1CB", borderRadius: 14,
            fontSize: 16, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 24px rgba(45,90,61,0.5)",
          }}
        >
          {lp.ctaBottom}
        </button>
        <div style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: "rgba(248,246,241,0.25)" }}>
          {lp.noCB}
        </div>
      </div>

    </div>
  );
}
